'use client';

import type { DataProps, Product, Props } from './product-list.component';
import {
  HeightSinceWidthEntry,
  PlaceholderConfig,
  PlaceholderContextValues
} from '@spa/lib/react/context/PlaceholderContext';
import { ComponentType } from 'react';
import { HeightSinceWidth } from '../placeholder/placeholder.component';
import { buildProductTeaserRenderPlan } from './buildProductTeaserRenderPlan';
import { calculateComponentChildrenPlaceholder } from '@spa/lib/calculateComponentChildrenPlaceholder';
import dynamic from 'next/dynamic';

const DynamicProductListComponent: ComponentType<Props> = dynamic(
  () => import('./product-list.component')
);

export default DynamicProductListComponent;

export function calculateProductListPlaceholderConfig(
  props: DataProps,
  contextValues: PlaceholderContextValues
): PlaceholderConfig {
  const products: readonly Product[] = props.products;
  const productsNumber: number = products.length;

  let baseHeightSinceWidth: HeightSinceWidth;
  if (productsNumber !== 0) {
    baseHeightSinceWidth = [
      { sinceWidth: 0, height: (productsNumber / 2) * 465 + 148 },
      { sinceWidth: 420, height: (productsNumber / 2) * 478 + 148 },
      { sinceWidth: 480, height: (productsNumber / 2) * 483 + 148 },
      { sinceWidth: 768, height: (productsNumber / 3) * 567 + 114 },
      { sinceWidth: 1024, height: (productsNumber / 3) * 591 + 114 },
      { sinceWidth: 1440, height: (productsNumber / 4) * 594 + 66 }
      // productsNumber is divided by the number of columns
      // then it's multiplied by the height of a product tile
      // and then the height of the section header is added.
    ];
  } else {
    baseHeightSinceWidth = [
      { sinceWidth: 0, height: 322 },
      { sinceWidth: 420, height: 288 },
      { sinceWidth: 768, height: 335 },
      { sinceWidth: 1024, height: 288 }
    ];
  }

  const teaserHeightSinceWidth = buildProductTeaserRenderPlan(
    props.productTeaser,
    products.length,
    props.editorMode
  ).entries.map(({ teaser }) => {
    return (
      calculateComponentChildrenPlaceholder(teaser.children, contextValues).heightSinceWidth ?? []
    );
  });

  const breakpoints = new Set(baseHeightSinceWidth.map((entry) => entry.sinceWidth));
  teaserHeightSinceWidth.forEach((entries) => {
    entries.forEach((entry) => breakpoints.add(entry.sinceWidth));
  });

  const heightSinceWidth: HeightSinceWidth = Array.from(breakpoints)
    .sort((a, b) => a - b)
    .map((breakpoint) => {
      const teaserHeight = teaserHeightSinceWidth.reduce((height, entries) => {
        return height + getHeightForBreakpoint(entries, breakpoint);
      }, 0);

      return {
        sinceWidth: breakpoint,
        height: getHeightForBreakpoint(baseHeightSinceWidth, breakpoint) + teaserHeight
      };
    });

  return {
    heightSinceWidth
  };
}

function getHeightForBreakpoint(
  entries: ReadonlyArray<HeightSinceWidthEntry>,
  breakpoint: number
): number {
  if (entries.length === 0) {
    return 0;
  }

  let height = entries[entries.length - 1].height;

  for (const entry of entries) {
    if (entry.sinceWidth <= breakpoint) {
      height = entry.height;
    } else {
      break;
    }
  }

  return height;
}
